import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

// ============================================================================
// MessageDialog — centered, in-app replacement for native window.alert().
// Native alert() is always pinned to the top of the viewport by the browser and
// cannot be styled or centered. This dialog follows the app modal standard
// (createPortal to body, fixed + centered, high z-index) so success/error
// messages appear centered over the current screen.
// ============================================================================

export type MessageVariant = 'success' | 'error' | 'warning' | 'info';

interface MessageDialogProps {
  open: boolean;
  message: string; // supports multi-line text (rendered with white-space: pre-line)
  variant?: MessageVariant;
  title?: string;
  okText?: string;
  onOk: () => void;
}

const VARIANT_CONFIG: Record<
  MessageVariant,
  { color: string; bg: string; symbol: string; defaultTitle: string }
> = {
  success: { color: '#10b981', bg: '#d1fae5', symbol: '✓', defaultTitle: 'Success' },
  error: { color: '#dc2626', bg: '#fee2e2', symbol: '✕', defaultTitle: 'Error' },
  warning: { color: '#f59e0b', bg: '#fef3c7', symbol: '!', defaultTitle: 'Warning' },
  info: { color: '#2563eb', bg: '#dbeafe', symbol: 'i', defaultTitle: 'Information' },
};

const MessageDialog: React.FC<MessageDialogProps> = ({
  open,
  message,
  variant = 'info',
  title,
  okText = 'OK',
  onOk,
}) => {
  // Close on Enter or Escape while the dialog is open.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        onOk();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onOk]);

  if (!open) return null;

  const cfg = VARIANT_CONFIG[variant];

  return createPortal(
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        zIndex: 10060,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        style={{
          position: 'relative',
          zIndex: 10061,
          width: '100%',
          maxWidth: 440,
          background: '#ffffff',
          borderRadius: 12,
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '24px 24px 16px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div
            aria-hidden
            style={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: cfg.bg,
              color: cfg.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {cfg.symbol}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3
              style={{
                margin: '2px 0 8px',
                fontSize: 16,
                fontWeight: 600,
                color: '#1f2937',
              }}
            >
              {title || cfg.defaultTitle}
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.5,
                color: '#4b5563',
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
              }}
            >
              {message}
            </p>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '12px 24px 20px',
          }}
        >
          <button
            type="button"
            autoFocus
            onClick={onOk}
            style={{
              minWidth: 96,
              padding: '9px 20px',
              border: 'none',
              borderRadius: 8,
              background: cfg.color,
              color: '#ffffff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {okText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MessageDialog;
